import { Inject, Injectable } from '@angular/core';
import { QueryResult } from './query-result';
import { AnalyticsService } from './analytics.service';
import { hashCode } from '../utils/string-hash';
import { IQueryService, QUERY_SERVICE_TOKEN } from './query/iquery.service';

export class CacheQuery {
    readonly queryRunner: () => Promise<QueryResult>;
    isQueryRunning: boolean = false;
    readonly subjects: ((value: QueryResult) => void)[] = [];
    subjectsCount = 0;
    expirationTime?: Date | null;
    hasResult: boolean = false;
    result?: QueryResult | null;

    constructor(queryRunner: () => Promise<QueryResult>) {
        // set fields
        this.queryRunner = queryRunner;
    }

    subscribe(): Promise<QueryResult> {
        var promise = new Promise<QueryResult>((res, rej) => {
            // check has result
            if (this.hasResult && new Date() < this.expirationTime!) {
                // return immediately
                res(this.result!);
                return;
            }

            // add to subscribers
            this.subjects.push(res);
            this.subjectsCount++;

            // check is query NOT running
            if (!this.isQueryRunning) {
                this.runQuery();
            }
        });

        return promise;
    }

    unsubscribe(postCancelCallback?: () => void) {
        this.subjectsCount--;

        // check if should cancel query
        if (this.subjectsCount <= 0) {
            // TODO cancel query
            if (postCancelCallback) postCancelCallback();
        }
    }

    private runQuery() {
        // clear fields
        this.isQueryRunning = true;
        this.hasResult = false;
        this.result = null;
        this.expirationTime = null;

        // run query
        this.queryRunner()
            // catch any error and pass it as result
            .catch((err) => ({ status: 'error', error: err } as QueryResult))
            .then((result) => {
                // assign result
                this.result = result;
                this.hasResult = true;
                this.isQueryRunning = false;
                this.expirationTime = new Date();
                this.expirationTime.setTime(
                    // add one hour
                    this.expirationTime.getTime() + 1000 * 60 * 60
                );

                // propagate results
                let subject: (value: QueryResult) => void;
                while (
                    (subject = this.subjects.pop() as (
                        value: QueryResult
                    ) => void)
                ) {
                    // callback subscriber
                    subject(this.result);
                }
            });
    }
}

@Injectable({
    providedIn: 'root',
})
export class QueryCacheService {
    private cacheQueries: { [key: string]: CacheQuery } = {};

    constructor(
        private analytics: AnalyticsService,
        @Inject(QUERY_SERVICE_TOKEN) private queryService: IQueryService
    ) {}

    public subscribeQuery<Type>(query: string): Promise<QueryResult> {
        // get hash
        // console.log({
        //     msg: 'FuncService.runQuery',
        //     query,
        // });
        const hash: string = hashCode(query);

        // check exists
        let cacheQuery = this.cacheQueries[hash];
        if (cacheQuery === undefined) {
            // not exists - create new
            cacheQuery = new CacheQuery(
                async () => await this.queryService.runQuery(query)
            );
            this.cacheQueries[hash] = cacheQuery;
        }

        return cacheQuery.subscribe();
    }

    public unsubscribeQuery(query: string): void {
        // get hash
        const hash: string = hashCode(query);
        // TODO analytics event
        const postCancelCallback = () => {};
        // cache should already exists
        this.cacheQueries[hash].unsubscribe(postCancelCallback);
    }

    public resetCache(): void {
        this.cacheQueries = {};
    }
}
